<template>
    <PageSidebar>
        <template #sidebarTitle> {{ $t("player.menu.files") }} </template>
        <template #sidebarCta>
            <div class="flex items-center space-x-2">
                <ButtonSecondaryIconSm @click="$emit('addFile', 'header')">
                    <FolderAddIcon class="ct-fg-2 hover:ct-fg-2" />
                </ButtonSecondaryIconSm>
                <ButtonSecondaryIconSm @click="$emit('addFile')">
                    <AddIcon class="ct-fg-2 hover:ct-fg-2" />
                </ButtonSecondaryIconSm>
            </div>
        </template>
        <template #sidebarMain>
            <div class="p-6 space-y-5">
                <draggable v-model="filesList" item-key="id" @change="order">
                    <template #item="{ element }">
                        <div class="relative mt-2 group" v-if="!element._deleted">
                            <FileCard
                                class="mb-3 ct-drag-item"
                                :class="[element.type !== 'header' ? 'ml-3' : '']"
                                :file="element"
                                @edit="editFile(element)"
                                @duplicate="cloneFile(element)"
                                @delete="askAndRemoveFile(element)"
                                @click="editFile(element)"
                            />
                            <DragDropIcon
                                class="absolute top-1/2 -left-3 -translate-y-1/2 ct-fg-4 opacity-0 group-hover:opacity-100"
                            />
                        </div>
                    </template>
                </draggable>

                <CardEmpty
                    class="w-full"
                    v-if="!notDeletedFiles.length"
                    :title="$t('player.empty.title')"
                    :icon="emptyIcon"
                    :ctaLabel="$t('player.empty.cta')"
                    @add="$emit('addFile')"
                />
            </div>
        </template>
        <template #sidebarFooter>
            <cSaveButton @save="saveSnapshot" :disabled="!hasChanges" />
        </template>
    </PageSidebar>
</template>

<script>
import draggable from "vuedraggable";
import { markRaw } from "vue";
import { mapState, mapMutations, mapActions } from "vuex";
import { PageSidebar } from "@easy/ui/page-components";
import { ButtonSecondaryIconSm } from "@easy/ui/buttons";
import { AddIcon, FolderAddIcon, DragDropIcon, MultipleFileIcon } from "@easy/ui/icons";
import { CardEmpty } from "@easy/ui/cards";
import FileCard from "./FileCard.vue";
import { useSaveOrDropModal } from "@/store/useSaveOrDropModal";
import cSaveButton from "@/shared/components/cSaveButton.vue";

export default {
    computed: {
        ...mapState("player", ["player", "files", "hasChanges"]),
        filesList: {
            get() {
                return this.files;
            },
            set(value) {
                this.setFiles(value);
            },
        },
        notDeletedFiles() {
            return this.files.filter((file) => !file._deleted);
        },
        emptyIcon() {
            return markRaw(MultipleFileIcon);
        },
    },
    components: {
        draggable,
        PageSidebar,
        FileCard,
        ButtonSecondaryIconSm,
        AddIcon,
        FolderAddIcon,
        DragDropIcon,
        CardEmpty,
        cSaveButton,
    },
    emits: ["editFile", "addFile"],
    setup() {
        return useSaveOrDropModal();
    },
    methods: {
        ...mapActions("player/projects", ["loadProject", "saveSnapshot"]),
        ...mapActions("player", ["cloneFile", "removeFile"]),
        ...mapActions("modals", ["showConfirmModal"]),
        ...mapMutations("player", ["setFiles"]),
        async editFile(file) {
            await this.askToConfirmIfNeeded({
                showModal: this.hasChanges,
                onConfirmChanges: async () => this.saveSnapshot(),
                onDropChanges: async () => this.loadProject(this.player.uuid),
            });
            this.$emit("editFile", file);
        },
        order() {
            this.setFiles();
        },
        async askAndRemoveFile(file) {
            const decision = await this.showConfirmModal({
                title: this.$t("player.files.delete_file_confirm"),
                description: this.$t("player.files.delete_file_message"),
                ctaLabel: this.$t("shared.delete"),
            });
            if (decision) {
                this.removeFile(file);
            }
        },
    },
};
</script>
